// src/pages/ApplicationChampionshipMatches/index.tsx
import { ChangeEvent, useEffect, useState } from 'react';
import { Button, Flex, Skeleton, Switch, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { GetParamsObject } from '@/model/query-params';
import { ApplicationComponentId, MatchV2 } from '@/model';
import { useDeleteMatchV2Mutation, useMatchesV2, usePutMatchV2Mutation } from '@/services/queries/match_v2';
import { PutMatchV2Request } from '@/services/request/match_v2';

import { breadcrumb } from './breadcrumb';
import { MatchesFilters } from './types';
import { MatchesFilterBox } from './components/MatchesFilterBox';
import { InlinePatch, MatchesTable } from './components/MatchesTable/MatchesTable';
import { MatchEditModal } from './components/MatchEditModal';
import { MatchCreateModal } from './components/MatchCreateModal';
import { MatchGenerateModal } from './components/MatchGenerateModal';
import { MatchSetCurrentRoundModal } from './components/MatchSetCurrentRoundModal';
import { MatchSetCurrentStageModal } from './components/MatchSetCurrentStageModal';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';

const I18N_PREFIX = 'pages.application_championship_matches';

export const ApplicationChampionshipMatches = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.GAME_SCHEDULE);
  const { mutate: enableAppAreaMutation, isPending: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isCurrentRoundModalOpen, setIsCurrentRoundModalOpen] = useState(false);
  const [isCurrentStageModalOpen, setIsCurrentStageModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MatchV2 | undefined>();

  const filtersForm = useFiltersForm<MatchesFilters>({
    defaultValues: {
      championship_uuid: '',
      season_uuid: '',
      stage_uuid: '',
      round: '',
      team_uuid: '',
      status: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({
    field: 'match_date',
    desc: true,
  });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: matchesData, isLoading, forceRefetch } = useMatchesV2(params);
  const items = matchesData?.data ?? [];

  const deleteMutation = useDeleteMatchV2Mutation();
  const putMutation = usePutMatchV2Mutation();

  /**
   * Auto-save inline: monta o PUT completo a partir do match atual + patch.
   * O backend exige todos os campos do payload, então preservamos tudo da
   * row e sobrescrevemos só o que mudou.
   */
  const handleInlineSave = (item: MatchV2, patch: InlinePatch) => {
    const stageUuid = item.stage?.uuid;
    if (!stageUuid) {
      toast({ status: 'error', title: t(`${I18N_PREFIX}.toasts.missing_stage`) });
      return;
    }

    const merged: MatchV2 = { ...item, ...patch };
    const payload: PutMatchV2Request = {
      stage_uuid: stageUuid,
      home_team_uuid: merged.home_team_uuid,
      away_team_uuid: merged.away_team_uuid,
      match_date: merged.match_date ?? null,
      stadium: merged.stadium ?? null,
      transmission: merged.transmission ?? null,
      hyperlink: merged.hyperlink ?? null,
      description: merged.description ?? null,
      round: merged.round ?? null,
      home_goals: merged.home_goals ?? null,
      away_goals: merged.away_goals ?? null,
      extra_time_home: merged.extra_time_home ?? null,
      extra_time_away: merged.extra_time_away ?? null,
      penalties_home: merged.penalties_home ?? null,
      penalties_away: merged.penalties_away ?? null,
      status: merged.status,
      is_show_information: merged.is_show_information,
    };

    putMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.inline_save_success`) });
          forceRefetch();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title: (error as Error)?.message || t(`${I18N_PREFIX}.toasts.inline_save_error`),
          });
        },
      },
    );
  };

  const handleDelete = (item: MatchV2) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;
    setIsConfirmModalOpen(false);
    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.delete_success`) });
        setSelectedItem(undefined);
        forceRefetch();
      },
      onError: (error: unknown) => {
        toast({
          status: 'error',
          title: (error as Error)?.message || t(`${I18N_PREFIX}.toasts.delete_error`),
        });
        setSelectedItem(undefined);
      },
    });
  };

  const handleEdit = (item: MatchV2) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const handleEditDone = () => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  };

  const handleCreateDone = () => {
    setIsCreateModalOpen(false);
    forceRefetch();
  };

  const handleGenerateDone = () => {
    setIsGenerateModalOpen(false);
    forceRefetch();
  };

  const handleCurrentRoundDone = () => {
    setIsCurrentRoundModalOpen(false);
  };

  const handleCurrentStageDone = () => {
    setIsCurrentStageModalOpen(false);
  };

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingAppAreaState(event.target.checked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successMsg = pendingAppAreaState
      ? t(`${I18N_PREFIX}.enable_success`)
      : t(`${I18N_PREFIX}.disable_success`);
    const errorMsg = pendingAppAreaState
      ? t(`${I18N_PREFIX}.enable_error`)
      : t(`${I18N_PREFIX}.disable_error`);

    setIsAppAreaActive(pendingAppAreaState);
    enableAppAreaMutation([{ component_id: ApplicationComponentId.GAME_SCHEDULE, active: pendingAppAreaState }], {
      onSuccess: () => toast({ status: 'success', title: successMsg }),
      onError: () => {
        toast({ status: 'error', title: errorMsg });
        setIsAppAreaActive(!pendingAppAreaState);
      },
    });
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;
    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t(`${I18N_PREFIX}.page_title`)} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} align={{ md: 'center' }} gap={4} mb={2}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t(`${I18N_PREFIX}.section_label`)}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive ? t(`${I18N_PREFIX}.switch_enabled`) : t(`${I18N_PREFIX}.switch_disabled`)}
                </Switch>
              </>
            )}
          </Flex>
          <Flex gap={2}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsCurrentStageModalOpen(true)}
              width={{ base: '100%', md: 'initial' }}
              variant='outline'
              colorScheme='brand'
            >
              <ChakraFeatherIcon name='git-branch' />
              {t(`${I18N_PREFIX}.current_stage_button`)}
            </Button>

            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsCurrentRoundModalOpen(true)}
              width={{ base: '100%', md: 'initial' }}
              variant='outline'
              colorScheme='brand'
            >
              <ChakraFeatherIcon name='flag' />
              {t(`${I18N_PREFIX}.current_round_button`)}
            </Button>

            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsGenerateModalOpen(true)}
              width={{ base: '100%', md: 'initial' }}
              variant='outline'
              colorScheme='brand'
            >
              <ChakraFeatherIcon name='zap' />
              {t(`${I18N_PREFIX}.generate_button`, 'Gerar automaticamente')}
            </Button>

            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsCreateModalOpen(true)}
              width={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t(`${I18N_PREFIX}.create_button`)}
            </Button>
          </Flex>
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <MatchesFilterBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <MatchesTable
            data={items}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={matchesData?.meta.pages || 0}
            totalItems={matchesData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={handleEdit}
            onDeleteClick={handleDelete}
            onInlineSave={handleInlineSave}
          />
        </Flex>
      </SectionBox>

      {selectedItem && isEditModalOpen ? (
        <MatchEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={handleEditDone}
        />
      ) : null}

      {isCreateModalOpen ? (
        <MatchCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onDone={handleCreateDone} />
      ) : null}

      {isGenerateModalOpen ? (
        <MatchGenerateModal
          isOpen={isGenerateModalOpen}
          onClose={() => setIsGenerateModalOpen(false)}
          onDone={handleGenerateDone}
        />
      ) : null}

      {isCurrentRoundModalOpen ? (
        <MatchSetCurrentRoundModal
          isOpen={isCurrentRoundModalOpen}
          onClose={() => setIsCurrentRoundModalOpen(false)}
          onDone={handleCurrentRoundDone}
        />
      ) : null}

      {isCurrentStageModalOpen ? (
        <MatchSetCurrentStageModal
          isOpen={isCurrentStageModalOpen}
          onClose={() => setIsCurrentStageModalOpen(false)}
          onDone={handleCurrentStageDone}
        />
      ) : null}

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={false}
        title={t(`${I18N_PREFIX}.delete_modal.title`)}
        description={
          <Text>
            {t(`${I18N_PREFIX}.delete_modal.description`)}{' '}
            <Text as='span' fontWeight='bold'>
              {selectedItem?.home_team?.name ?? '?'} × {selectedItem?.away_team?.name ?? '?'}
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t(`${I18N_PREFIX}.toggle_modal_title`)}
        description={
          <Text>
            {pendingAppAreaState
              ? t(`${I18N_PREFIX}.enable_modal_description`)
              : t(`${I18N_PREFIX}.disable_modal_description`)}
          </Text>
        }
      />

      <CenteredSpinner isLoading={deleteMutation.isPending || putMutation.isPending || isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
